/**
 * Progress saved in the browser (localStorage). Per layout: the stars earned on each
 * lesson, the adaptive key model, and a short history of finished runs for the charts.
 * Everything is per-viewer and best-effort — every read and write is guarded so a
 * private window or blocked storage never breaks the tool.
 */
const KEY = 'sohozkaj.typing.v1';

const empty = () => ({
    prefs: {},
    layouts: {
        english: { lessons: {}, model: null, history: [], settings: {} },
        bijoy: { lessons: {}, model: null, history: [], settings: {} },
        avro: { lessons: {}, model: null, history: [], settings: {} },
    },
});

export function loadProgress() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return empty();
        const data = JSON.parse(raw);
        const base = empty();
        return {
            prefs: data.prefs || legacyPrefs(data.layouts?.bijoy?.settings),
            layouts: {
                english: { ...base.layouts.english, ...(data.layouts?.english || {}) },
                bijoy: { ...base.layouts.bijoy, ...(data.layouts?.bijoy || {}) },
                avro: { ...base.layouts.avro, ...(data.layouts?.avro || {}) },
            },
        };
    } catch {
        return empty();
    }
}

/** Older saves kept the display toggles per layout; carry them over once. Their
 *  `sound` (a beep on a wrong key) is dropped — the new key sounds start on. */
function legacyPrefs(old = {}) {
    const out = {};
    ['showKeyboard', 'fingerColors', 'dimKeys'].forEach((k) => { if (typeof old[k] === 'boolean') out[k] = old[k]; });
    return out;
}

export function saveProgress(data) {
    try {
        localStorage.setItem(KEY, JSON.stringify(data));
    } catch {
        /* private window / quota — progress simply isn't kept */
    }
}

/** Record a lesson result, keeping the best star count. */
export function recordLesson(data, layoutId, lessonId, result) {
    const layout = data.layouts[layoutId];
    const prev = layout.lessons[lessonId];
    const best = !prev || result.stars > prev.stars ? result : prev;
    const lessons = { ...layout.lessons, [lessonId]: { ...best, lastWpm: result.wpm, lastAccuracy: result.accuracy, at: Date.now() } };
    return { ...data, layouts: { ...data.layouts, [layoutId]: { ...layout, lessons } } };
}

export function recordRun(data, layoutId, run) {
    const layout = data.layouts[layoutId];
    const history = [...(layout.history || []), run].slice(-50);
    return { ...data, layouts: { ...data.layouts, [layoutId]: { ...layout, history } } };
}

export function saveModel(data, layoutId, model) {
    const layout = data.layouts[layoutId];
    return { ...data, layouts: { ...data.layouts, [layoutId]: { ...layout, model } } };
}

export function saveSettings(data, layoutId, settings) {
    const layout = data.layouts[layoutId];
    return { ...data, layouts: { ...data.layouts, [layoutId]: { ...layout, settings: { ...layout.settings, ...settings } } } };
}

/** Tool-wide preferences (sound and display) — shared by both layouts. */
export function savePrefs(data, patch) {
    return { ...data, prefs: { ...(data.prefs || {}), ...patch } };
}

/** Wipe all progress — stars, lessons, run history and the adaptive key model of
 *  both layouts — but keep the sound and display preferences. */
export function resetProgress(data) {
    return { ...empty(), prefs: data.prefs || {} };
}
